import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  AlertTriangle, 
  Loader2, 
  Shield, 
  Clock, 
  Wrench, 
  HardHat,
  Target,
  Radio,
  MapPin,
  Phone,
  User,
  Calendar,
  Clipboard,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Truck,
  Mail,
  X,
  ArrowLeft
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const PublicOperatorOrder = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [company, setCompany] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Enable scrolling for this page
  useEffect(() => {
    document.body.classList.add('public-proposal-page');
    return () => {
      document.body.classList.remove('public-proposal-page');
    };
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/operator-order/${token}`);
      setOrderData(res.data);
      setCompany(res.data.company);
    } catch (error) {
      console.error("Failed to load operator order", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Calculate stats
  const tradeSections = orderData?.trade_sections || [];
  const totalTasks = tradeSections.reduce((sum, sec) => sum + (sec.tasks?.length || 0), 0);
  const preMissionChecklist = orderData?.pre_mission_checklist || [];
  const postMissionChecklist = orderData?.post_mission_checklist || [];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Loader2 className="h-12 w-12 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Not found state
  if (!orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Toaster position="top-center" />
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Operator Order Not Found</h1>
          <p className="text-slate-400">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif",
        overflowY: "auto",
        overflowX: "hidden",
        height: "auto",
        minHeight: "100vh"
      }}
    >
      <Toaster position="top-center" />
      
      {/* Close/Back Button - Fixed at top */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => {
            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.close();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Back"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <button
          onClick={() => window.close()}
          className="flex items-center justify-center w-10 h-10 bg-slate-800/90 hover:bg-red-600 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Close Tab"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Document Container */}
      <div 
        className="mx-auto"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 1: HEADER - Company Info & Mission Metadata
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(59, 130, 246, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Info */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ 
                color: "#3b82f6",
                fontFamily: "'Playfair Display', serif"
              }}
            >
              {company?.company_name || orderData?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {company?.address || "Licensed & Insured Contractor"}
            </p>
          </div>
          
          {/* Mission Metadata */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">MISSION ID</p>
              <p 
                className="text-white font-bold text-sm"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {orderData?.order_id || "OPR-00001"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">ISSUE DATE</p>
              <p className="text-white font-bold text-sm">{formatDate(orderData?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">PRIORITY</p>
              <p 
                className="font-bold text-sm px-2 py-0.5 rounded"
                style={{ 
                  backgroundColor: orderData?.priority === "URGENT" ? "rgba(239, 68, 68, 0.2)" : 
                                   orderData?.priority === "HIGH" ? "rgba(245, 158, 11, 0.2)" : "rgba(59, 130, 246, 0.2)", 
                  color: orderData?.priority === "URGENT" ? "#ef4444" : 
                         orderData?.priority === "HIGH" ? "#f59e0b" : "#60a5fa"
                }}
              >
                {orderData?.priority || "STANDARD"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">STATUS</p>
              <p 
                className="font-bold text-sm px-2 py-0.5 rounded"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", color: "#10b981" }}
              >
                {orderData?.status || "READY FOR DEPLOYMENT"}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 2: MISSION TITLE & STATS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Target className="h-5 w-5 text-blue-400" />
            <p className="text-xs uppercase tracking-[4px] text-blue-400">OPERATOR ORDER</p>
          </div>
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}
          >
            {orderData?.project_name || "Field Operation Brief"}
          </h2>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)" }}
            >
              <p className="text-2xl font-bold text-blue-400">{tradeSections.length}</p>
              <p className="text-xs text-slate-500 uppercase">Trade Sections</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}
            >
              <p className="text-2xl font-bold text-emerald-400">{totalTasks}</p>
              <p className="text-xs text-slate-500 uppercase">Task Items</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
            >
              <p className="text-2xl font-bold text-amber-400">{orderData?.estimated_duration || "TBD"}</p>
              <p className="text-xs text-slate-500 uppercase">Est. Duration</p>
            </div>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}
            >
              <p className="text-2xl font-bold text-red-400">{tradeSections.length}</p>
              <p className="text-xs text-slate-500 uppercase">Safety Briefs</p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 3: JOB SITE INTELLIGENCE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="h-4 w-4 text-blue-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">JOB SITE INTELLIGENCE</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Client Info */}
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">CLIENT CONTACT</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-500" />
                  <span className="text-white font-medium">{orderData?.site_contact || "Client Name"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{orderData?.contact_phone || "No phone on file"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{orderData?.contact_email || "No email on file"}</span>
                </div>
              </div>
            </div>
            
            {/* Site Location */}
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">JOB SITE LOCATION</p>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                  <span className="text-white font-medium">{orderData?.site_address || "Address TBD"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{orderData?.site_access || "7:00 AM - 6:00 PM"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{orderData?.staging || "As directed by client"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 4: PRE-MISSION CHECKLIST
            ═══════════════════════════════════════════════════════════════════════════ */}
        {preMissionChecklist.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clipboard className="h-4 w-4 text-amber-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PRE-MISSION CHECKLIST</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {preMissionChecklist.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-3 rounded"
                  style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: item.checked ? "rgba(16, 185, 129, 0.2)" : "rgba(100, 116, 139, 0.2)",
                      border: item.checked ? "1px solid #10b981" : "1px solid rgba(100, 116, 139, 0.5)"
                    }}
                  >
                    {item.checked && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                  </div>
                  <span className={`text-sm ${item.checked ? "text-slate-500 line-through" : "text-slate-300"}`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 5: TRADE SECTIONS - Work Scope
            ═══════════════════════════════════════════════════════════════════════════ */}
        {tradeSections.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-6">
              <HardHat className="h-4 w-4 text-blue-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">TACTICAL OPERATIONS BY TRADE</p>
            </div>
            
            <div className="space-y-4">
              {tradeSections.map((section, sectionIdx) => {
                const isExpanded = expandedSections[`section-${sectionIdx}`] !== false;
                return (
                  <div 
                    key={sectionIdx}
                    className="rounded-lg overflow-hidden"
                    style={{ border: "1px solid rgba(59, 130, 246, 0.3)" }}
                  >
                    {/* Section Header - Collapsible */}
                    <button
                      onClick={() => toggleSection(`section-${sectionIdx}`)}
                      className="w-full px-5 py-4 flex items-center justify-between"
                      style={{ backgroundColor: "rgba(59, 130, 246, 0.15)" }}
                    >
                      <div className="flex items-center gap-3">
                        <Wrench className="h-5 w-5 text-blue-400" />
                        <span className="text-white font-bold text-lg">{section.name}</span>
                        <span className="text-slate-500 text-sm">({section.tasks?.length || 0} tasks)</span>
                      </div>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </button>
                    
                    {/* Section Content */}
                    {isExpanded && (
                      <div className="px-5 py-4 space-y-4" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                        {/* Tasks */}
                        {section.tasks && section.tasks.length > 0 && (
                          <div>
                            <p className="text-xs text-emerald-400 uppercase tracking-widest mb-2">EXECUTION TASKS</p>
                            <div className="space-y-2">
                              {section.tasks.map((task, taskIdx) => (
                                <div 
                                  key={taskIdx}
                                  className="flex items-start gap-3 p-3 rounded"
                                  style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}
                                >
                                  <span className="text-blue-400 font-mono text-sm">{String(taskIdx + 1).padStart(2, '0')}</span>
                                  <span className="text-slate-300 text-sm">{task}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Required Tools */}
                        {section.requiredTools && section.requiredTools.length > 0 && (
                          <div>
                            <p className="text-xs text-amber-400 uppercase tracking-widest mb-2">REQUIRED TOOLS & EQUIPMENT</p>
                            <div className="flex flex-wrap gap-2">
                              {section.requiredTools.map((tool, toolIdx) => (
                                <span 
                                  key={toolIdx}
                                  className="px-3 py-1 rounded text-sm"
                                  style={{ backgroundColor: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}
                                >
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Safety Protocols */}
                        {section.safetyProtocols && section.safetyProtocols.length > 0 && (
                          <div>
                            <p className="text-xs text-red-400 uppercase tracking-widest mb-2">SAFETY PROTOCOLS</p>
                            <div className="space-y-1">
                              {section.safetyProtocols.map((protocol, pIdx) => (
                                <div key={pIdx} className="flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-slate-400 text-sm">{protocol}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* QC Checklist */}
                        {section.qcChecklist && section.qcChecklist.length > 0 && (
                          <div>
                            <p className="text-xs text-purple-400 uppercase tracking-widest mb-2">QUALITY CONTROL</p>
                            <div className="space-y-1">
                              {section.qcChecklist.map((qc, qIdx) => (
                                <div key={qIdx} className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded flex items-center justify-center"
                                    style={{ 
                                      backgroundColor: qc.checked ? "rgba(16, 185, 129, 0.2)" : "rgba(100, 116, 139, 0.2)",
                                      border: qc.checked ? "1px solid #10b981" : "1px solid rgba(100, 116, 139, 0.5)"
                                    }}
                                  >
                                    {qc.checked && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                                  </div>
                                  <span className="text-slate-400 text-sm">{qc.text}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 6: POST-MISSION CHECKLIST
            ═══════════════════════════════════════════════════════════════════════════ */}
        {postMissionChecklist.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">POST-MISSION CHECKLIST</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {postMissionChecklist.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-3 rounded"
                  style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: item.checked ? "rgba(16, 185, 129, 0.2)" : "rgba(100, 116, 139, 0.2)",
                      border: item.checked ? "1px solid #10b981" : "1px solid rgba(100, 116, 139, 0.5)"
                    }}
                  >
                    {item.checked && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                  </div>
                  <span className={`text-sm ${item.checked ? "text-slate-500 line-through" : "text-slate-300"}`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            FOOTER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 py-6 text-center"
          style={{ 
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-sm">
            {company?.company_name || "Alpha Construction Pros"} • Licensed & Insured
          </p>
          <p className="text-slate-600 text-xs mt-1">
            This operator order is confidential and intended for authorized personnel only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicOperatorOrder;
